import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Flashcard from "../../components/Flashcard";
import { Row, Col } from "reactstrap";
import "../../assets/css/SidebarComponents/Flashcard.css";
import { useDarkMode } from "context/DarkModeProvider";
import "../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";
import axiosInstance from "apiHandles/axiosInstance";
import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY

function decryptData(encryptedData) {
  try {
    const key = CryptoJS.SHA256(SECRET_KEY);
    const decrypted = CryptoJS.AES.decrypt(
      encryptedData,
      key,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
}

const FlashcardsPage = () => {
  const s = localStorage.getItem("structureLocation");

  const colorSchemes = {
    HashTable: { primary: '#10b981', secondary: '#059669', accent: '#d1fae5' },
    Tree: { primary: '#3b82f6', secondary: '#2563eb', accent: '#dbeafe' },
    List: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#ede9fe' },
    Stack: { primary: '#f59e0b', secondary: '#d97706', accent: '#fef3c7' },
    Queue: { primary: '#ef4444', secondary: '#dc2626', accent: '#fecaca' },
    Graph: { primary: '#06b6d4', secondary: '#0891b2', accent: '#cffafe' },
    Array: { primary: '#ec4899', secondary: '#db2777', accent: '#fce7f3' },
    Set: { primary: '#84cc16', secondary: '#65a30d', accent: '#ecfccb' },
    Python: { primary: '#3776ab', secondary: '#ffd43b' }

  };

  const scheme = colorSchemes[s] || colorSchemes.Graph;

  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { selLanguage, toggleLanguage, isLoading: langLoading } = useLanguage();

  const [totalFlashcards, setTotalFlashcards] = useState(0);
  const [currentFlashcard, setCurrentFlashcard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  // Cache για τα prefetched flashcards
  const flashcardCache = useRef({});

  // Fetch metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const selectedStageId = localStorage.getItem("selectedStageId");
        if (!selectedStageId) {
          console.error("No stage ID found in localStorage");
          navigate("/admin/stages");
          return;
        }
        const response = await axiosInstance.get(`/api/stage/${selectedStageId}/flashcards/`);
        const decryptedData = decryptData(response.data.data);
        setTotalFlashcards(decryptedData.total_flashcards);
      } catch (error) {
        if (error.response?.status === 401) {
          alert(selLanguage ? "Πρέπει να κάνεις login ξανά" : "You need to login again");
          localStorage.removeItem("access");
          navigate("/login");
        } else if (error.response?.status === 404) {
          console.warn("No flashcards found for this stage");
          setTotalFlashcards(0);
        } else {
          setTotalFlashcards(0);
        }
      }
    };
    fetchMetadata();
  }, [navigate, selLanguage]);

  // Function για fetch ενός flashcard
  const fetchFlashcard = async (index) => {
    // Αν υπάρχει στο cache, επιστροφή από εκεί
    if (flashcardCache.current[index]) {
      return flashcardCache.current[index];
    }

    try {
      const selectedStageId = localStorage.getItem("selectedStageId");
      const response = await axiosInstance.get(
        `/api/stage/${selectedStageId}/flashcard/${index}/`
      );

      const encryptedData = response.data.data;
      if (!encryptedData) {
        console.error("No encrypted data received");
        return null;
      }

      const decryptedData = decryptData(encryptedData);
      if (!decryptedData) {
        console.error("Decryption failed");
        return null;
      }

      const flashcard = {
        front: decryptedData.question_text,
        back: decryptedData.answer_text
      };

      // Αποθήκευση στο cache
      flashcardCache.current[index] = flashcard;
      return flashcard;

    } catch (error) {
      console.error("Error fetching flashcard:", error);
      return null;
    }
  };

  // Prefetch του επόμενου flashcard
  const prefetchNext = async (currentIndex) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < totalFlashcards && !flashcardCache.current[nextIndex]) {
      await fetchFlashcard(nextIndex);
    }
  };

  // Prefetch του προηγούμενου flashcard
  const prefetchPrev = async (currentIndex) => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0 && !flashcardCache.current[prevIndex]) {
      await fetchFlashcard(prevIndex);
    }
  };

  // Load current flashcard και prefetch γειτόνων
  useEffect(() => {
    if (totalFlashcards === 0) return;

    const loadFlashcard = async () => {
      setIsLoading(true);

      // Φόρτωση του τρέχοντος flashcard
      const flashcard = await fetchFlashcard(currentCardIndex);

      if (flashcard) {
        setCurrentFlashcard(flashcard);
      } else {
        alert("Σφάλμα κατά τη φόρτωση του flashcard");
      }

      setIsLoading(false);

      // Prefetch επόμενου και προηγούμενου στο background
      prefetchNext(currentCardIndex);
      prefetchPrev(currentCardIndex);
    };

    loadFlashcard();
  }, [currentCardIndex, totalFlashcards]);

  const handleCardClick = () => {
    setIsFlipped((prev) => !prev);
  };

  const handleNextCard = () => {
    if (currentCardIndex < totalFlashcards - 1) {
      setCurrentCardIndex((prevIndex) => prevIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prevIndex) => prevIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleCorrect = () => {
    setCorrectCount((prev) => prev + 1);
    nextOrFinish();
  };

  const handleWrong = () => {
    nextOrFinish();
  };

  const nextOrFinish = () => {
  if (currentCardIndex === totalFlashcards - 1) {
    setIsFinished(true);
  } else {
    setIsFlipped(false);          
    setCurrentCardIndex(prev => prev + 1);
  }
};

  // Loading state
  if (isLoading || (totalFlashcards > 0 && !currentFlashcard)) {
    return (
      <div className="flashcards-page" style={{
        background: darkMode ? "#0f172a" : "transparent",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh"
      }}>
        <div style={{
          color: darkMode ? "#f1f5f9" : "#1e293b",
          fontSize: "1.2rem"
        }}>
          Loading flashcard...
        </div>
      </div>
    );
  }

  // No flashcards state
  if (totalFlashcards === 0) {
    return (
      <div className="flashcards-page" style={{
        background: darkMode ? "#0f172a" : "transparent",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh"
      }}>
        <div style={{
          color: darkMode ? "#f1f5f9" : "#1e293b",
          fontSize: "1.2rem",
          textAlign: "center"
        }}>
          <p>No flashcards available for this stage.</p>
          <button
            onClick={() => navigate(-1)}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              background: scheme.primary,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const score = Math.floor((correctCount / totalFlashcards) * 1000) / 10;

  let scoreClass = "low";
  if (score >= 80) {
    scoreClass = "high";
  } else if (score >= 40) {
    scoreClass = "medium";
  }

  if (isFinished) {
    let stars = 0;
    if (score >= 80) {
      stars = 3;
    } else if (score >= 40) {
      stars = 2;
    } else if (score >= 10) {
      stars = 1;
    }

    return (
      <div className="flashcards-page"
        style={{ background: darkMode ? "#0f172a" : "transparent" }}>
        <div className="final-score"
          style={{
            background: darkMode ? "#1e293b" : "rgb(248,250,252)",
          }}>
          <div className="stars">
            {[...Array(3)].map((_, i) => (
              <span key={i} className={`star ${i < stars ? 'yellow' : 'white'}`}>★</span>
            ))}
          </div>

          <div className="score-message">
            {score >= 80 ? (
              <p>{selLanguage ? "Καταπληκτική δουλειά! Τα πήγες περίφημα." : "Amazing work! You did great."}</p>
            ) : score >= 40 ? (
              <p>{selLanguage ? "Πολύ καλή δουλειά! Συνέχισε έτσι." : "Very good work! Keep it up."}</p>
            ) : (
              <p>{selLanguage ? "Καλή προσπάθεια! Συνέχισε την εξάσκηση." : "Good effort! Keep practicing."}</p>
            )}
          </div>

          <button
            className="try-againFlashcard"
            style={{
              background: darkMode ? "#334155" : "#f8fafc",
              border: darkMode ? "2px solid #475569" : "none",
              color: darkMode ? "#f8fafc" : "#494949"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = darkMode ? "#475569" : "#f0f0f0";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = darkMode ? "#334155" : "#f8fafc";
            }}
            onClick={() => {
              // Clear cache on retry
              flashcardCache.current = {};
              setCurrentCardIndex(0);
              setCorrectCount(0);
              setIsFinished(false);
              setIsFlipped(false);
            }}
          >
            <i className="fas fa-redo" style={{ marginRight: "8px" }} />
            {selLanguage ? "Παίξε ξανά" : "Try Again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Col className="flashcards-page" style={{ background: 'transparent' }}>
      <Row className="flashcard-wrapper">
        <button
          className="moderButtonFlashcard desktop-only"
          style={{
            visibility: currentCardIndex > 0 ? 'visible' : 'hidden',
            background: darkMode ? "#1e293b" : `${scheme.primary}95`,
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = darkMode ? "#475569" : `${scheme.primary}cc`;
            e.target.style.borderColor = "rgba(96, 165, 250, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = darkMode ? "#334155" : `${scheme.primary}95`;
            e.target.style.borderColor = "rgba(148, 163, 184, 0.2)";
          }}
          onClick={handlePrevCard}
        >
          <i className="fas fa-arrow-left" />
        </button>

        <Col className="flashcard-container" xl="10" lg="9" md="10" sm="10" xs="12"
         style={{ '--card-color': `${scheme.primary}` }}>
          <div className="card-counter" style={{ color: darkMode ? "#f1f5f9" : "#1e293b" }}>
            {currentCardIndex + 1} / {totalFlashcards}
          </div>
          <Flashcard
            key={currentCardIndex}
            front={currentFlashcard.front}
            back={currentFlashcard.back}
            flipped={isFlipped}
            onFlip={handleCardClick}
            onCorrect={handleCorrect}
            onWrong={handleWrong}
          />
        </Col>

        <button
          className="moderButtonFlashcard desktop-only"
          style={{
            visibility: currentCardIndex < totalFlashcards - 1 ? 'visible' : 'hidden',
            background: darkMode ? "#1e293b" : `${scheme.primary}95`,
          }}
          onMouseEnter={(e) => {
            if (currentCardIndex < totalFlashcards - 1) {
              e.target.style.backgroundColor = darkMode ? "#475569" : `${scheme.primary}cc`;
              e.target.style.borderColor = "rgba(96, 165, 250, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = darkMode ? "#334155" : `${scheme.primary}95`;
            e.target.style.borderColor = "rgba(148, 163, 184, 0.2)";
          }}
          onClick={currentCardIndex < totalFlashcards - 1 ? handleNextCard : undefined}
        >
          <i className="fas fa-arrow-right" />
        </button>

        <div className="flashcard-navigation-bottom">
          <button
            className="moderButtonFlashcard"
            style={{
              visibility: currentCardIndex > 0 ? 'visible' : 'hidden',
              background: darkMode ? "#1e293b" : `${scheme.primary}95`,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = darkMode ? "#475569" : `${scheme.primary}cc`;
              e.target.style.borderColor = "rgba(96, 165, 250, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = darkMode ? "#334155" : `${scheme.primary}95`;
              e.target.style.borderColor = "rgba(148, 163, 184, 0.2)";
            }}
            onClick={handlePrevCard}
          >
            <i className="fas fa-arrow-left" />
          </button>

          <button
            className="moderButtonFlashcard"
            style={{
              visibility: currentCardIndex < totalFlashcards - 1 ? 'visible' : 'hidden',
              background: darkMode ? "#1e293b" : `${scheme.primary}95`,
            }}
            onMouseEnter={(e) => {
              if (currentCardIndex < totalFlashcards - 1) {
                e.target.style.backgroundColor = darkMode ? "#475569" : `${scheme.primary}cc`;
                e.target.style.borderColor = "rgba(96, 165, 250, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = darkMode ? "#334155" : `${scheme.primary}95`;
              e.target.style.borderColor = "rgba(148, 163, 184, 0.2)";
            }}
            onClick={currentCardIndex < totalFlashcards - 1 ? handleNextCard : undefined}
          >
            <i className="fas fa-arrow-right" />
          </button>
        </div>
      </Row>
    </Col>
  );
};

export default FlashcardsPage;